import styled from "styled-components";
import { theme } from "twin.macro";

import { SPACE } from "../LayoutTokens";

/** The default `code` mark: a quiet inline chip that keeps the line intact. */
export const StyledCode = styled.code`
  padding: 0 ${theme("spacing.1")};
  border-radius: ${theme("borderRadius.DEFAULT")};
  background-color: var(--color-theme-300);
  font-family: "SFMono-Regular", Menlo, Consolas, "Liberation Mono", monospace;
  font-size: 0.85em;
`;

StyledCode.displayName = "StyledCode";

/** The default `link` mark: an inline text link inside flowing content. */
export const StyledLink = styled.a`
  color: var(--color-primary);
  text-decoration: underline var(--color-primary);
  text-underline-offset: 3px;

  &:hover {
    text-decoration: none;
  }
`;

StyledLink.displayName = "StyledLink";

/**
 * The default `code-block`: quiet preformatted content. It stays a bare
 * `<pre><code>` on purpose — article surfaces inject a Copy button onto
 * every `<pre>` they render.
 */
export const StyledCodeBlock = styled.pre`
  margin: 0;
  padding: ${SPACE.md};
  border-radius: ${theme("borderRadius.lg")};
  background-color: var(--color-theme-200);
  overflow-x: auto;

  code {
    font-family: "SFMono-Regular", Menlo, Consolas, "Liberation Mono", monospace;
    font-size: ${theme("fontSize.body-2")};
    color: var(--color-theme-900);
  }
`;

StyledCodeBlock.displayName = "StyledCodeBlock";

/**
 * The default `table` block wrapper: Table ships page-level chrome (outer
 * margins) that flowing content does not want — the PortableText Stack owns
 * the vertical rhythm here.
 */
export const StyledTableBlock = styled.div`
  .c-table__wrap {
    margin: 0;
  }
`;

StyledTableBlock.displayName = "StyledTableBlock";

/** The default `divider` block: a themed horizontal rule. */
export const StyledDivider = styled.hr`
  margin: 0;
  border: none;
  border-top: 1px solid var(--border-primary);
`;

StyledDivider.displayName = "StyledDivider";

/**
 * The default `list` block; rendered as ul or ol through the `as` prop, and
 * tagged `data-portable-list` so the depth rules below scope to design-system
 * lists — a stray `<ul>`/`<ol>` from an injected renderer is left untouched.
 *
 * Nesting works structurally: each level is its own StyledList inside a parent
 * `<li>`, so it re-applies its own `padding-left` (indentation compounds) and,
 * for ordered levels, restarts numbering. A nested list carries no vertical
 * margin, so it hugs the item it belongs to.
 *
 * Both bullet and numbered levels change their marker by nesting depth, so
 * levels stay distinct even where the two interleave:
 *   bullets   disc → circle → square → (disc)
 *   numbers   decimal → lower-alpha → lower-roman → (upper-alpha)
 * Depth is counted from the list ancestors themselves (not per-tag), so an
 * intervening `<ol>` never throws off a `<ul>`'s depth and vice-versa. The
 * rules are ordered shallow-to-deep; a deeper rule is more specific, so it
 * wins the cascade for the levels it applies to.
 */
export const StyledList = styled.ul<{ $ordered: boolean }>`
  margin: 0;
  padding-left: ${theme("spacing.5")};
  list-style: ${({ $ordered }) => ($ordered ? "decimal" : "disc")} outside;

  li + li {
    margin-top: ${SPACE.xs};
  }

  /* Depth 2 */
  & ul[data-portable-list] {
    list-style-type: circle;
  }
  & ol[data-portable-list] {
    list-style-type: lower-alpha;
  }

  /* Depth 3 */
  & [data-portable-list] ul[data-portable-list] {
    list-style-type: square;
  }
  & [data-portable-list] ol[data-portable-list] {
    list-style-type: lower-roman;
  }

  /* Depth 4 (the producer's cap; deeper content clamps into it) */
  & [data-portable-list] [data-portable-list] ul[data-portable-list] {
    list-style-type: disc;
  }
  & [data-portable-list] [data-portable-list] ol[data-portable-list] {
    list-style-type: upper-alpha;
  }
`;

StyledList.displayName = "StyledList";
